import { Injectable } from '@angular/core';

/**
 * IndexedDB Storage Service
 * Simplified storage for encrypted encryption keys
 */
@Injectable({
  providedIn: 'root',
})
export class IndexedDBStorageService {
  private readonly DB_NAME = 'arm_secure_storage';
  private readonly STORE_NAME = 'encryption_keys';
  private readonly KEY_NAME = 'encryption_key';
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Get or initialize database connection
   */
  private getDB(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, 1);
      request.onerror = () => reject(new Error('Failed to open IndexedDB'));
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME);
        }
      };
    });

    return this.dbPromise;
  }

  /**
   * Execute a database operation with automatic error handling
   */
  private async execute<T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T | null> {
    try {
      const db = await this.getDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.STORE_NAME], mode);
        const store = transaction.objectStore(this.STORE_NAME);
        const request = operation(store);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () =>
          reject(
            new Error(request.error?.message || 'Database operation failed')
          );
      });
    } catch {
      return null;
    }
  }

  /**
   * Store encrypted key
   */
  async setEncryptedKey(key: string): Promise<void> {
    await this.execute('readwrite', store => store.put(key, this.KEY_NAME));
  }

  /**
   * Retrieve encrypted key
   */
  async getEncryptedKey(): Promise<string | null> {
    return this.execute('readonly', store => store.get(this.KEY_NAME));
  }

  /**
   * Remove encrypted key
   */
  async removeEncryptedKey(): Promise<void> {
    await this.execute('readwrite', store => store.delete(this.KEY_NAME));
  }

  /**
   * Clear all data
   */
  async clearAll(): Promise<void> {
    await this.execute('readwrite', store => store.clear());
  }
}
